import { bearing } from '@turf/bearing';
import { distance } from '@turf/distance';
import { point } from '@turf/helpers';

/**
 * Convert geographic distance to screen pixels using turf.js
 * @param fromLat Starting latitude in degrees
 * @param fromLng Starting longitude in degrees
 * @param toLat Destination latitude in degrees
 * @param toLng Destination longitude in degrees
 * @param metersPerPixel Current map scale in meters per pixel
 * @returns Object with deltaX and deltaY in pixels for screen positioning
 */
export function getPixelOffsetFromCoordinates(
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number,
  metersPerPixel: number
): { deltaX: number; deltaY: number } {
  // Create turf points
  const fromPoint = point([fromLng, fromLat]);
  const toPoint = point([toLng, toLat]);

  // Calculate distance and bearing using turf
  const distanceInKm = distance(fromPoint, toPoint, { units: 'kilometers' });
  const distanceInMeters = distanceInKm * 1000;
  const bearingInDegrees = bearing(fromPoint, toPoint);
  const bearingInRadians = (bearingInDegrees * Math.PI) / 180;

  // Convert to pixels
  const distanceInPixels = distanceInMeters / metersPerPixel;

  // Calculate X/Y components (screen coordinates: +X = right, +Y = down)
  const deltaX = Math.sin(bearingInRadians) * distanceInPixels;
  const deltaY = -Math.cos(bearingInRadians) * distanceInPixels; // negative because screen Y increases downward

  return { deltaX, deltaY };
}
